import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { X } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type FlatSpecies = {
  name: string;
  nameFr: string;
  id: number;
  nameEn: string;
};

type SelectedSpeciesInput = Partial<FlatSpecies> & {
  species?: Partial<FlatSpecies>;
};

const normalizeSelectedSpecies = (
  values: SelectedSpeciesInput[] | null | undefined,
): FlatSpecies[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((entry) => {
      const source = entry?.species ?? entry;
      const name = source?.name?.toString().trim() ?? "";
      if (!name) {
        return null;
      }

      return {
        name,
        nameFr: source?.nameFr?.toString().trim() || name,
        nameEn: source?.nameEn?.toString().trim() || name,
        id: Number(source?.id) || 0,
      };
    })
    .filter((item): item is FlatSpecies => null !== item);
};

const PlantInput: FC<{
  handleChange: (
    values: { name: string; nameFr: string; nameEn: string; id: number }[],
  ) => void;
  selected: SelectedSpeciesInput[] | null | undefined;
  withMargin?: boolean;
  isReadOnly?: boolean;
  speciesType?: 1 | 2;
}> = ({
  handleChange,
  selected,
  withMargin = true,
  isReadOnly = false,
  speciesType = 2,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<
    {
      name: string;
      image?: string;
      nameFr?: string;
      nameEn?: string;
      id: number;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<
    { name: string; nameFr: string; id: number; nameEn: string }[]
  >([]);
  const hidePostActions = useHidePostActions();
  const hasAccessToEdit = true;
  const cacheRef = useRef<
    Map<
      string,
      {
        name: string;
        image?: string;
        nameFr?: string;
        nameEn?: string;
        id: number;
      }[]
    >
  >(new Map());
  const { i18n } = useTranslation();
  useEffect(() => {
    setSelectedOptions(normalizeSelectedSpecies(selected));
  }, [selected]);
  useEffect(() => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }

    const cacheKey = `${speciesType}:${normalizedQuery.toLowerCase()}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setResults(cached);
      return;
    }

    const controller = new AbortController();

    const fetchResults = async () => {
      setLoading(true);
      const token =
        localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      try {
        const endpointBase = import.meta.env.VITE_API_ENDPOINT;
        const query = `type=${speciesType}&s=${encodeURIComponent(normalizedQuery)}`;

        // Backward compatible: try fast endpoint first, then fallback to existing API route.
        let response = await fetch(`${endpointBase}/species-search-fast?${query}`, {
          method: "GET",
          headers,
          signal: controller.signal,
        });

        if (!response.ok) {
          response = await fetch(`${endpointBase}/species/search?${query}`, {
            method: "GET",
            headers,
            signal: controller.signal,
          });
        }

        if (!response.ok) {
          setResults([]);
          return;
        }

        const data = await response.json();
        const members = data["hydra:member"] || [];
        cacheRef.current.set(cacheKey, members);
        setResults(members);
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          return;
        }
        console.error("Erreur lors de la récupération des résultats :", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchResults();
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(delayDebounceFn);
    };
  }, [query, speciesType]);

  const addItem = (
    item:
      | {
          name: string;
          nameFr?: string | null;
          id?: number | string | null;
          nameEn?: string | null;
        }
      | string,
  ) => {
    if (typeof item === "string") {
      const itemTemp = {
        name: item,
        nameFr: item,
        id: 0, // Génère un ID aléatoire
        nameEn: item,
      };
      setSelectedOptions([...selectedOptions, itemTemp]);
      handleChange([...selectedOptions, itemTemp]);
      return;
    }
    if (!selectedOptions.some((selected) => selected.name === item?.name)) {
      const itemTemp = {
        name: item.name,
        nameFr: item.nameFr || item.name,
        id: Number(item.id) || 0,
        nameEn: item.nameEn || item.name,
      };
      setSelectedOptions([...selectedOptions, itemTemp]);
      handleChange([...selectedOptions, itemTemp]);
    }
    setQuery(""); // Réinitialise le champ d'entrée
    setResults([]); // Vide les résultats affichés
  };

  const removeItem = (i: {
    name: string;
    nameFr: string;
    id: number;
    nameEn: string;
  }) => {
    setSelectedOptions(selectedOptions.filter((item) => item.name !== i.name));
    handleChange(selectedOptions.filter((item) => item.name !== i.name));
  };

  const { t } = useTranslation("dashboard", {
    keyPrefix: "plant",
  });

  return (
    <div className={`mx-auto w-full ${withMargin ? "mt-10" : ""}`}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onBlur={() =>
          setTimeout(() => {
            setResults([]);
            setQuery("");
          }, 200)
        }
        disabled={isReadOnly || !hasAccessToEdit || hidePostActions}
        placeholder={t("Commencez à taper...")}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      />
      {loading && <p className="mt-2 text-sm text-gray-500">Chargement...</p>}
      {!loading && query && (
        <ul className="posi mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
          {/* Ajouter comme nouveau */}
          {query && (
            <li
              className="cursor-pointer border-b px-4 py-2 text-green-600 last:border-b-0 hover:bg-green-100"
              onMouseDown={(e) => {
                e.preventDefault();
                addItem(query);
              }}
            >
              {t("+ Ajouter")} <span className="font-semibold">"{query}"</span>{" "}
              {t("comme nouvel essence")}
            </li>
          )}
          {results?.map(
            (
              result: {
                name: string;
                image?: string;
                nameFr?: string;
                nameEn?: string;
                id: number;
              },
              index,
            ) =>
              !selectedOptions?.some(
                (selected) => selected.name === result.name,
              ) && (
                <li
                  key={index}
                  className="flex cursor-pointer gap-2 border-b px-4 py-2 last:border-b-0 hover:bg-blue-100"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addItem(result);
                  }}
                >
                  {!result.image && <div className="size-6"></div>}
                  {result.image && (
                    <div className="size-6">
                      <img
                        src={result.image}
                        alt={result.name}
                        height={40}
                        className="size-full object-cover"
                      />
                    </div>
                  )}
                  <span>
                    {i18n.language === "fr" && result.nameFr
                      ? result.nameFr
                      : i18n.language === "en" && result.nameEn
                        ? result.nameEn
                        : result.name}
                  </span>
                </li>
              ),
          )}
        </ul>
      )}
      {/* Badges des éléments sélectionnés */}
      {selectedOptions?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedOptions?.map(
            (item) =>
              item.name !== "" && (
                <div
                  key={item.name}
                  className="flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm text-verdeoblue shadow-sm"
                >
                  <span>
                    {(i18n.language === "fr" ? item.nameFr : item.nameEn) ||
                      item.name}
                  </span>
                  {!isReadOnly && (
                    <button
                      onClick={() => removeItem(item)}
                      className="ml-2 text-red-500 hover:text-verdeored"
                    >
                      <X />
                    </button>
                  )}
                </div>
              ),
          )}
        </div>
      )}
    </div>
  );
};

export default PlantInput;
